import { AnyAction } from 'redux';
import { opApiRequest } from 'src/api/apiRequest';
import endpoints from 'src/api/opEndpoints';
import apiTypes from 'src/api/optimalprint-sdk.d';
import { Store } from 'src/store/index';
import { ThunkDispatch } from 'redux-thunk';

interface AddonCounter {
  clipArtId: string; // Changed to string to support UUIDs
  quantity: number;
}

// Fallback product ID in case API doesn't return one (should not happen)
const DEFAULT_CLIPART_PRODUCT_ID = 3914;

// Helper to map PublicAsset response to ClipArt format
const mapPublicAssetToClipArt = (
  asset: any,
  productId: number,
): apiTypes.AppBundle.Api.Response.Customer.Design.Design.V3.CustomerDesignGetClipArtsV3Response.IClipArt => {
  // Find preview file
  const previewFile = asset.files?.find((f: any) => f.type === 'preview_default');

  return {
    clipArtId: asset.id, // UUID string
    title: asset.name,
    description: asset.description || null,
    imageUrl: previewFile?.url || '',
    productId, // Product ID from backend API
    tags: asset.tags || null,
    clipArtCategoryIds: asset.category?.categoryId ? [asset.category.categoryId] : null,
    allowColorChange: true, // Default value for public assets
    quantity: 1, // Default quantity, will be updated later
  };
};

const fetchClipArts = async (clipArtIds: string[]) => {
  try {
    // Try new Public Asset API first (uses existing /list endpoint with ids parameter)
    const fullResponse = (await opApiRequest(
      endpoints.PUBLIC_ASSET_V1_LIST,
      {
        ids: clipArtIds,
      },
      'data',
      'GET',
    )) as { assets: any[]; productId?: number } | null;

    if (fullResponse && fullResponse.assets && fullResponse.assets.length > 0) {
      // Get productId from API response, fallback to default
      const productId = fullResponse.productId ?? DEFAULT_CLIPART_PRODUCT_ID;
      const mappedAssets = fullResponse.assets.map((asset) => mapPublicAssetToClipArt(asset, productId));
      return mappedAssets;
    }

    // Fallback to old API if needed (for backward compatibility during migration)
    // Convert string IDs to numbers if they are numeric
    const numericIds = clipArtIds
      .filter((id) => !Number.isNaN(Number(id)))
      .map((id) => Number(id));

    if (numericIds.length > 0) {
      const clipArtsInUse = (await opApiRequest(
        endpoints.CLIP_ART_V1_GET,
        {
          clipArtIds: numericIds,
        },
        'data.clipArts',
        'POST',
      )) as apiTypes.AppBundle.Api.Response.Customer.Design.Design.V3.CustomerDesignGetClipArtsV3Response.IClipArt[];
      return clipArtsInUse;
    }

    return undefined;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Could not fetch clip arts by ids, exception:', e);
    return undefined;
  }
};

const fetchClipArtsOperation = (clipArtIds: AddonCounter[]) => async (dispatch: ThunkDispatch<Store, undefined, AnyAction>) => {
  // Skip API call if no cliparts to fetch
  if (!clipArtIds || clipArtIds.length === 0) {
    return [];
  }

  const clipArtsInUse = await fetchClipArts(clipArtIds.map((ca) => ca.clipArtId));
  if (clipArtsInUse) {
    clipArtsInUse.forEach((ca) => {
      const foundAddon = clipArtIds.find((caq) => String(ca.clipArtId) === String(caq.clipArtId));
      ca.quantity = foundAddon?.quantity || 1;
    });
  }

  return clipArtsInUse;
};

export default fetchClipArtsOperation;
